import React, { useContext, useEffect, useState } from "react";
import { LogOut, X, LayoutDashboard } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";

import { Button } from "src/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "src/components/ui/avatar";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "src/components/ui/tooltip";

import { AuthContext } from "src/providers/AuthProvider";
import { useActiveUser } from "src/hooks/useActiveUser";
import { SidebarMenuItems } from "./SidebarMenuItems";

import logoIcon from "src/assets/logo-sacgpt.png";
import "./Sidebar.css";

/**
 * AdminSidebar - Compact sidebar for Admin users
 * Based on Partner Dashboard pill design but simplified (no assistant mode)
 */
const AdminSidebar = ({ menuOpen = false, toggleMenu }) => {
  const { user } = useActiveUser();
  const { signOut } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 900);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 900);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Get user initials for avatar
  const getUserInitials = (user) => {
    if (user?.name) {
      return user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }
    return user?.email?.slice(0, 2).toUpperCase() || 'AD';
  };

  const getUserDisplayName = (user) => {
    return user?.name || user?.email || 'Administrador';
  };

  // Hide sidebar on mobile when menu is not open
  if (isMobile && !menuOpen) {
    return null;
  }

  return (
    <>
      {/* Mobile overlay */}
      {isMobile && (
        <div
          className={`fixed inset-0 bg-black/50 z-40 lg:hidden sidebar-overlay ${menuOpen ? 'overlay-visible' : 'overlay-hidden'}`}
          onClick={toggleMenu}
        />
      )}

      {/* Sidebar Container */}
      <div
        className={`sidebar-container-v2 ${isMobile ? 'mobile-sidebar' : ''} ${isMobile && menuOpen ? 'mobile-open' : ''}`}
      >
        {/* Unified Sidebar Pill */}
        <div className="sidebar-v2 pill-admin-mode">
          {/* Header with Logo Icon */}
          <div className="sidebar-header-v2">
            {isMobile ? (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-sidebar-icon hover:text-white hover:bg-white/10"
                onClick={toggleMenu}
              >
                <X className="h-5 w-5" />
              </Button>
            ) : (
              <a href="/admin/dashboard" className="flex items-center justify-center w-full">
                <img
                  src={logoIcon}
                  alt="SacGPT Admin"
                  className="h-7 w-7"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.parentElement.innerHTML = '<span class="text-white font-bold text-lg">S</span>';
                  }}
                />
              </a>
            )}
          </div>

          {/* Home/Dashboard Button */}
          <TooltipProvider delayDuration={0}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  className={`sidebar-nav-item-v2 sidebar-home-btn ${location.pathname === '/admin/dashboard' ? 'active' : ''}`}
                  onClick={() => navigate("/admin/dashboard")}
                >
                  <LayoutDashboard className="w-5 h-5" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right" sideOffset={10}>
                <p>Dashboard</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          {/* Navigation Items */}
          <nav className="sidebar-nav-v2">
            <SidebarMenuItems isCompact={true} currentPath={location.pathname} excludeHome={true} />
          </nav>

          {/* Footer - User Profile + Logout */}
          <div className="sidebar-footer-v2">
            <TooltipProvider delayDuration={0}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button className="sidebar-user-btn">
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={user?.avatar} alt={getUserDisplayName(user)} />
                      <AvatarFallback className="bg-primary text-white font-semibold text-xs">
                        {getUserInitials(user)}
                      </AvatarFallback>
                    </Avatar>
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right" sideOffset={10}>
                  <div>
                    <p className="font-semibold">{getUserDisplayName(user)}</p>
                    <p className="text-xs text-muted-foreground">Administrador</p>
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            <TooltipProvider delayDuration={0}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    className="sidebar-logout-btn"
                    onClick={() => signOut()}
                  >
                    <LogOut className="w-5 h-5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right" sideOffset={10}>
                  <p>Sair do sistema</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminSidebar;
